import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { insertFurnitureIntoCache, spliceErefIntoCache } from "helpers/quotationCache.helper"
import { useCreateFurniture } from "hooks/api/quotation/quotations.hook"
import AnnotationService from "services/annotations.service"
import type { TQuotationDetail } from "types/quotations.type"
import type { TCreateFurnitureRequest, TFurniture, TFurnitureCustomization } from "types/elevationReferences.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import type { TAreaCreateSession } from "./useAreaCreate"
import { useAreaCreate } from "./useAreaCreate"
import { useApplyCustomization } from "./useApplyCustomization"

type Props = {
	createSessionRef: MutableRefObject<TAreaCreateSession | null>
	draftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	appContainerRef: MutableRefObject<HTMLElement | null>
	getViewerPanes: () => TPaneBinding[]
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	onFurnitureCreated: (furniture: TFurniture) => void
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

export const useCountertopCreate = ({
	createSessionRef,
	draftRectRef,
	appContainerRef,
	getViewerPanes,
	layoutEpoch,
	numericQuotationIdRef,
	queryClient,
	onFurnitureCreated,
	onToolChange,
	clearSelectionToolbar,
	success,
	formatError,
}: Props) => {
	const { mutateAsync: createFurnitureAsync } = useCreateFurniture()
	const applyCustomization = useApplyCustomization(formatError)
	const [isCreatePending, setIsCreatePending] = useState(false)
	const areaCreate = useAreaCreate({
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})

	const runCreateCountertop = useCallback(
		async ({
			width,
			height,
			depth,
			categoryId,
			erefId,
			erefVersion,
			customization,
		}: {
			width: number
			height: number
			depth: number
			categoryId: number
			erefId: number | null
			erefVersion: number | null
			/**
			 * REQ-413/414. Null/absent unless the panel's Customization section is expanded AND
			 * complete — the endpoint is per-object, so it can only be applied after the create.
			 */
			customization?: TFurnitureCustomization | null
		}) => {
			const quotationId = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (quotationId == null || Number.isNaN(quotationId) || !area) return
			setIsCreatePending(true)
			try {
				const body: TCreateFurnitureRequest = {
					page_number: area.pageNumber,
					x0: area.coordinates.x0,
					y0: area.coordinates.y0,
					x1: area.coordinates.x1,
					y1: area.coordinates.y1,
					related_category_id: categoryId,
					eref_id: erefId,
					...(erefId !== null && erefVersion !== null ? { eref_version: erefVersion } : {}),
					width,
					height,
					depth,
				}
				const response = await createFurnitureAsync({ quotationId, body })
				const createdFurniture = response.data?.this_furniture
				if (!createdFurniture) return
				// The one extra call the customization costs (REQ-413/414), against the version the
				// create returned. Best-effort: past this line the object exists no matter how it went.
				const created = await applyCustomization(quotationId, createdFurniture, customization ?? null)
				let isInCache = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					let next = insertFurnitureIntoCache(cached, created)
					isInCache = next !== cached
					if (response.data.updated_eref) next = spliceErefIntoCache(next, response.data.updated_eref)
					return next
				})
				if (!isInCache) queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				AnnotationService.addFurnitureAnnotation(created)
				areaCreate.cancelCreateArea()
				onFurnitureCreated(created)
				success("Countertop created")
			} catch (error) {
				formatError(error)
			} finally {
				setIsCreatePending(false)
			}
		},
		[areaCreate, applyCustomization, createFurnitureAsync, createSessionRef, formatError, numericQuotationIdRef, onFurnitureCreated, queryClient, success],
	)

	return {
		createSession: areaCreate.createSession,
		isCreatePending,
		startCreateCountertop: areaCreate.startCreateArea,
		cancelCreateCountertop: areaCreate.cancelCreateArea,
		runCreateCountertop,
	}
}
